(() => {
  'use strict';
  const root = document.querySelector('[data-binder-builder-config]');
  if (!root) return;
  const normalize = text => String(text || '').normalize('NFKC').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
  const base = window.Shopify?.routes?.root || '/';
  async function request(path, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 25000);
    try {
      const response = await fetch(path, {...options, signal: controller.signal});
      const data = await response.json();
      if (!response.ok) throw Error(data.description || 'This request could not be confirmed. Please retry.');
      return data;
    } finally { clearTimeout(timer); }
  }
  async function graphql(query, variables = {}) {
    const data = await request('/api/2026-07/graphql.json', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({query, variables})});
    if (data.errors?.length) throw Error('Current stock could not be checked. Please retry.');
    return data.data;
  }
  async function cart() {
    if (window.KillinKiosk) return {items: window.KillinKiosk.items.map(item => ({variant_id: item.id, quantity: item.quantity}))};
    return request(base + 'cart.js');
  }
  const counts = cart => new Map((cart.items || []).map(item => [String(item.variant_id), Number(item.quantity)]));
  async function stock(keys, condition, progress = () => {}) {
    const cards = await window.KillinBinderCatalog.resolve(keys);
    const links = cards.filter(Boolean).flatMap(card => card.listings.filter(link => condition === 'ANY' || link.condition === condition));
    const ids = [...new Set(links.map(link => link.variant))];
    const variants = new Map();
    const country = root.dataset.country || 'CA';
    const query = `query($ids:[ID!]!,$country:CountryCode!) @inContext(country:$country){nodes(ids:$ids){...on ProductVariant{
      id title availableForSale price{amount currencyCode} product{title handle productType}}}}`;
    for (let offset = 0; offset < ids.length; offset += 40) {
      const data = await graphql(query, {ids: ids.slice(offset, offset + 40), country});
      for (const variant of data.nodes.filter(Boolean)) variants.set(variant.id, variant);
      progress(Math.min(offset + 40, ids.length), ids.length);
    }
    return cards.map((card, index) => {
      if (!card) return {key: keys[index], card: null, reason: 'Catalog card unavailable'};
      const candidates = card.listings.filter(link => condition === 'ANY' || link.condition === condition).map(link => ({link, variant: variants.get(link.variant)})).filter(({link, variant}) =>
        variant?.availableForSale && variant.product.handle === link.handle &&
        /^Trading Card Singles?$/i.test(variant.product.productType) && normalize(variant.product.title) === normalize(link.title));
      candidates.sort((a, b) => Number(a.variant.price.amount) - Number(b.variant.price.amount) || a.variant.id.localeCompare(b.variant.id));
      const match = candidates[0];
      if (!match) return {key: card.key, card, reason: 'No matching copy available'};
      const id = match.variant.id.split('/').pop();
      return {key: card.key, card, id, variantGid: match.variant.id, handle: match.link.handle,
        condition: match.link.condition, price: Number(match.variant.price.amount), currency: match.variant.price.currencyCode,
        reason: '', variant: match.variant};
    });
  }
  async function review(keys, condition, progress = () => {}) {
    const rows = await stock(keys, condition, progress);
    const existing = counts(await cart());
    return rows.map(row => existing.has(row.id) ? {...row, reason: 'Already in your cart'} : row);
  }
  const availabilityCache = new Map();
  async function availability(keys) {
    const missing = [...new Set(keys)].filter(key => !availabilityCache.has(key) || availabilityCache.get(key).expires <= Date.now());
    if (missing.length) {
      const request = stock(missing, 'ANY');
      missing.forEach((key, index) => {
        const entry = {expires: Date.now() + 30000, result: null};
        entry.result = request.then(rows => rows[index]).catch(error => {
          if (availabilityCache.get(key) === entry) availabilityCache.delete(key);
          throw error;
        });
        availabilityCache.set(key, entry);
      });
    }
    return Promise.all(keys.map(key => availabilityCache.get(key).result));
  }
  async function add(rows, condition, progress = () => {}) {
    const kioskRevision = window.KillinKiosk?.revision;
    const selected = rows.filter(row => row.id && !row.reason);
    const fresh = await review(selected.map(row => row.key), condition, progress);
    if (window.KillinKiosk && kioskRevision !== window.KillinKiosk.revision) return {added: [], missing: [], message: 'The kiosk session ended. Start a new selection.'};
    const changed = fresh.filter((row, index) => !row.reason && (row.id !== selected[index].id || row.price !== selected[index].price || row.currency !== selected[index].currency));
    if (changed.length) return {needsReview: true, rows: fresh, added: [], missing: [], message: 'Prices or available copies changed. Review the updated selection before adding.'};
    const pending = fresh.filter(row => row.id && !row.reason);
    const added = [], missing = fresh.filter(row => row.reason && row.reason !== 'Already in your cart');
    if (window.KillinKiosk) {
      const revision = window.KillinKiosk.revision;
      for (const row of pending) {
        if (window.KillinKiosk.revision !== revision) break;
        try {
          await window.KillinKiosk.add({id: row.id, available: true, title: row.variant.title, price: Math.round(row.price * 100)}, {title: row.variant.product.title});
          added.push(row);
        } catch (error) { missing.push({...row, reason: error.message}); }
      }
      return {added, missing};
    }
    for (let offset = 0; offset < pending.length; offset += 20) {
      const before = counts(await cart());
      const batch = pending.slice(offset, offset + 20).filter(row => !before.has(row.id));
      if (!batch.length) continue;
      let error;
      try {
        await request(base + 'cart/add.js', {method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({items: batch.map(row => ({id: row.id, quantity: 1}))})});
      } catch (caught) { error = caught; }
      // A timeout or 422 may follow a partial write. Read the cart before any retry.
      let latest;
      try { latest = await cart(); }
      catch (_) { return {added, missing, uncertain: true, message: 'Some additions could not be confirmed. Open your cart before trying again.'}; }
      const after = counts(latest);
      for (const row of batch) {
        if ((after.get(row.id) || 0) > (before.get(row.id) || 0)) added.push(row);
        else missing.push({...row, reason: error?.message || 'This copy could not be added'});
      }
      document.querySelectorAll('.cart-link span').forEach(span => { span.textContent = String(latest.item_count); });
      document.dispatchEvent(new CustomEvent('killin:cart-change', {detail: latest}));
      progress(Math.min(offset + 20, pending.length), pending.length);
    }
    return {added, missing};
  }
  async function digest(text) {
    const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)));
    return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  }
  async function transfer(binder, owned) {
    const selected = new Set(binder.order.map(id => binder.slots[id].choice).filter(Boolean));
    const ownership = Object.fromEntries(Object.entries(owned).filter(([key]) => selected.has(key)));
    const payload = JSON.stringify({schema: 1, binder, owned: ownership});
    const characters = Array.from(payload), parts = [];
    for (let offset = 0; offset < characters.length; offset += 2000) parts.push(characters.slice(offset, offset + 2000).join(''));
    if (parts.length > 220) throw Error('This binder is too large to transfer. Split it into smaller binders.');
    const meta = {schema: 1, parts: parts.length, digest: await digest(payload)};
    const attributes = [{key: '_kb_transfer', value: JSON.stringify(meta)}, ...parts.map((value, index) => ({key: '_kb_' + index, value}))];
    const data = await graphql(`mutation($input:CartInput!){cartCreate(input:$input){cart{id} userErrors{message}}}`, {input: {attributes}});
    if (data.cartCreate.userErrors.length || !data.cartCreate.cart) throw Error('Could not prepare the phone transfer. Please retry.');
    const url = new URL('/pages/binder', location.origin);
    url.searchParams.set('mode', 'builder');
    if (new URL(location.href).searchParams.get('builder_preview') === '1') url.searchParams.set('builder_preview', '1');
    url.hash = 'kb=' + encodeURIComponent(data.cartCreate.cart.id);
    return url.href;
  }
  async function receiveTransfer(id) {
    if (!id || id.length > 500 || !id.startsWith('gid://shopify/Cart/')) throw Error('Invalid binder transfer.');
    const data = await graphql(`query($id:ID!){cart(id:$id){createdAt attributes{key value}}}`, {id});
    const remote = data.cart;
    const created = Date.parse(remote?.createdAt);
    if (!remote || !Number.isFinite(created) || created > Date.now() || Date.now() - created > 86400000) throw Error('This transfer expired. Create a new QR code on the kiosk.');
    const attributes = Object.fromEntries(remote.attributes.map(item => [item.key, item.value]));
    let meta;
    try { meta = JSON.parse(attributes._kb_transfer); } catch (_) { throw Error('This binder transfer has already been collected or is unavailable.'); }
    if (meta.schema !== 1 || !Number.isInteger(meta.parts) || meta.parts < 1 || meta.parts > 220) throw Error('Invalid binder transfer.');
    const payload = Array.from({length: meta.parts}, (_, index) => attributes['_kb_' + index] || '').join('');
    if (await digest(payload) !== meta.digest) throw Error('The transfer is incomplete. Create a fresh QR code.');
    const parsed = JSON.parse(payload), binder = window.KillinBinderModel.normalizeBinder(parsed.binder);
    if (!binder) throw Error('Invalid binder transfer.');
    return {binder, owned: window.KillinBinderModel.normalize({schema: 1, owned: parsed.owned}).owned,
      async clear() {
        const clean = remote.attributes.filter(item => item.key.startsWith('_kb_')).map(item => ({key: item.key, value: ''}));
        const cleared = await graphql(`mutation($id:ID!,$attributes:[AttributeInput!]!){cartAttributesUpdate(cartId:$id,attributes:$attributes){userErrors{message}}}`, {id, attributes: clean});
        if (cleared.cartAttributesUpdate.userErrors.length) throw Error('Your draft was imported, but the transfer link could not be cleared.');
      }};
  }
  window.KillinBinderShopping = {review, add, availability, transfer, receiveTransfer};
})();
